/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'utils/toastUtils'
import { useAppDispatch } from 'helpers/store'
import { useTypedSelector } from 'helpers/reducers'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import { ReservationCheckoutPlan } from 'types/entities/ReservationCheckoutPlan'
import { ShoppingCartItemPaymentType } from 'types/enums/ShoppingCartItemPaymentType'
import SellerAction from 'store/seller/SellerAction'
import ArticlesAction from 'store/articles/ArticlesAction'
import { OffersAction } from 'store/offers/OffersAction'
import UiTextarea from 'components/Ui/UiTextarea'
import { CheckIcon } from '@heroicons/react/24/outline'
import ReservationCheckoutPlannerModalItem
    from './ReservationCheckoutPlannerModalComponents/ReservationCheckoutPlannerModalItem'

interface ReservationCheckoutPlannerModalProps {
    reservationId?: number
    offerId?: number
    initialPlans?: ReservationCheckoutPlan[]
    initialInvoiceNote?: string | null
    initialOfferNote?: string | null
}

const FALLBACK_ARTICLE_GROUPS = [
    { id: 1000, name: 'Eintritt' },
    { id: 1001, name: 'Karting' },
    { id: 1005, name: 'Event Pauschal' },
    { id: 1006, name: 'Event Upsells' },
    { id: 1008, name: 'Standard Upsells' },
    { id: 1010, name: 'Event Package Items' },
    { id: 1011, name: 'Event Ticket Upgrades' },
    { id: 2000, name: 'Getränke AFG' },
    { id: 2050, name: 'Energy Drinks' },
    { id: 2100, name: 'Kaffee/Tee' },
    { id: 2200, name: 'Getränke Alkohol' },
    { id: 2210, name: 'Bier' },
]

const ReservationCheckoutPlannerModal: React.FC<
    ReservationCheckoutPlannerModalProps
> = ({
    reservationId,
    offerId,
    initialPlans = [],
    initialInvoiceNote,
    initialOfferNote,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const articleGroups = useTypedSelector(
        (state) => state.articlesState.articleGroups
    )
    const selectedOffer = useTypedSelector(
        (state) => state.offersState.selectedOffer
    )
    const seller_id = useTypedSelector(
        (state) => state.sessionState?.session?.uid
    )

    const groupsToUse =
        articleGroups.length > 0 ? articleGroups : FALLBACK_ARTICLE_GROUPS

    const [plans, setPlans] = useState<ReservationCheckoutPlan[]>([])
    const [invoiceNote, setInvoiceNote] = useState(initialInvoiceNote || '')
    const [offerNote, setOfferNote] = useState(initialOfferNote || '')
    const [isSaving, setIsSaving] = useState(false)

    const handlePaymentTypeChange = (
        articleGroupId: number,
        paymentType: ShoppingCartItemPaymentType
    ) => {
        setPlans((prevPlans) =>
            prevPlans.map((plan) =>
                plan.article_group_id === articleGroupId
                    ? { ...plan, payment_type: paymentType }
                    : plan
            )
        )
    }

    const handleActiveToggle = (articleGroupId: number) => {
        setPlans((prevPlans) =>
            prevPlans.map((plan) =>
                plan.article_group_id === articleGroupId
                    ? { ...plan, is_active: !plan.is_active }
                    : plan
            )
        )
    }

    const handleChangeAllPaymentTypes = (
        paymentType: ShoppingCartItemPaymentType
    ) => {
        setPlans((prevPlans) =>
            prevPlans.map((plan) => ({ ...plan, payment_type: paymentType }))
        )
    }

    const handleChangeAllStatus = (isActive: boolean) => {
        setPlans((prevPlans) =>
            prevPlans.map((plan) => ({ ...plan, is_active: isActive }))
        )
    }

    const handleSave = async () => {
        setIsSaving(true)
        try {
            if (offerId) {
                await dispatch(
                    OffersAction.updateOffer({
                        id: offerId,
                        seller_id: seller_id ?? undefined,
                        extra: {
                            ...selectedOffer?.extra,
                            reservation_plan:
                                selectedOffer?.extra?.reservation_plan || [],
                            reservation_checkout_plan: plans,
                            invoice_note: invoiceNote || null,
                            offer_note: offerNote || null,
                        },
                    })
                )
            } else if (reservationId) {
                await Promise.all([
                    dispatch(
                        SellerAction.fetchSetReservationCheckoutPlan(
                            reservationId,
                            plans
                        )
                    ),
                    dispatch(
                        SellerAction.fetchSetInvoiceNoteToReservation(
                            reservationId,
                            invoiceNote || null
                        )
                    ),
                    dispatch(
                        SellerAction.fetchSetOfferNoteToReservation(
                            reservationId,
                            offerNote || null
                        )
                    ),
                ])
            }
            toast.success(t('Reservation checkout plan has been created.'))
        } catch {
            toast.error(t('Reservation checkout plan failed.'))
        } finally {
            setIsSaving(false)
        }
    }

    const getArticleGroupName = (id: number): string => {
        return groupsToUse.find((g) => g.id === id)?.name || `${id}`
    }

    useEffect(() => {
        if (articleGroups.length === 0) {
            dispatch(ArticlesAction.fetchArticleGroups())
        }
    }, [dispatch, articleGroups.length])

    useEffect(() => {
        const initializedPlans = groupsToUse.map((group) => {
            const existingPlan = initialPlans.find(
                (p) => p.article_group_id === group.id
            )
            if (existingPlan) {
                return {
                    ...existingPlan,
                    is_active: existingPlan.is_active ?? true,
                }
            }
            return {
                article_group_id: group.id,
                payment_type: ShoppingCartItemPaymentType.Checkout,
                is_active: false,
            }
        })
        setPlans(initializedPlans)
    }, [groupsToUse, initialPlans])

    const headerBottom = (
        <div className="flex items-center gap-3">
            <UiButton
                title={t('Plan speichern')}
                size={UiButtonSize.Medium}
                style={UiButtonStyle.Primary}
                className="bg-[#0c1b4d]! hover:bg-[#1a2d6b]! rounded-md"
                icon={<CheckIcon className="w-4 h-4" />}
                onClick={handleSave}
                disabled={isSaving}
            />
        </div>
    )

    const headerActions = (
        <div className="flex items-center gap-3">
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded p-0.5">
                <button
                    type="button"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded text-xs font-medium transition-colors"
                    onClick={() =>
                        handleChangeAllPaymentTypes(
                            ShoppingCartItemPaymentType.Checkout
                        )
                    }
                    title={t('Change all to cash payment')}
                >
                    {t('Kasse')}
                </button>
                <button
                    type="button"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded text-xs font-medium transition-colors"
                    onClick={() =>
                        handleChangeAllPaymentTypes(
                            ShoppingCartItemPaymentType.Invoice
                        )
                    }
                    title={t('Change all to invoice payment')}
                >
                    {t('Rechnung')}
                </button>
            </div>
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded p-0.5">
                <button
                    type="button"
                    className="bg-green-600 hover:bg-green-700 text-white px-2.5 py-1 rounded text-xs font-medium transition-colors"
                    onClick={() => handleChangeAllStatus(true)}
                    title={t('Activate all')}
                >
                    {t('Active')}
                </button>
                <button
                    type="button"
                    className="bg-red-600 hover:bg-red-700 text-white px-2.5 py-1 rounded text-xs font-medium transition-colors"
                    onClick={() => handleChangeAllStatus(false)}
                    title={t('Deactivate all')}
                >
                    {t('Inactive')}
                </button>
            </div>
        </div>
    )

    const footerContent = (
        <div className="w-full p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-2 gap-4">
                <UiTextarea
                    label={t('Offer note')}
                    value={offerNote}
                    onChange={(e) => setOfferNote(e.target.value)}
                    rows={3}
                    placeholder={t('Enter offer note...') as string}
                />
                <UiTextarea
                    label={t('Invoice note')}
                    value={invoiceNote}
                    onChange={(e) => setInvoiceNote(e.target.value)}
                    rows={3}
                    placeholder={t('Enter invoice note...') as string}
                />
            </div>
        </div>
    )

    return (
        <UiFormModalShell
            className="ReservationCheckoutPlannerModal"
            title={t('Zahlungsplan')}
            showHeaderCompact
            headerBottom={headerBottom}
            headerActions={headerActions}
            bodyClassName="bg-white dark:bg-gray-950 p-6"
            footer={footerContent}
        >
            <div className="flex flex-col gap-3">
                {plans.map((plan) => (
                    <ReservationCheckoutPlannerModalItem
                        key={plan.article_group_id}
                        plan={plan}
                        getArticleGroupName={getArticleGroupName}
                        handlePaymentTypeChange={handlePaymentTypeChange}
                        handleActiveToggle={handleActiveToggle}
                    />
                ))}
            </div>
        </UiFormModalShell>
    )
}

export default ReservationCheckoutPlannerModal
